import axios from "axios";

export const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

api.interceptors.request.use((c) => {
  const t = localStorage.getItem("pb_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

export const ACCESS_ERROR_KEY = "pb_access_error";
export const REVOKED_PREFIX = "Seu acesso foi revogado";

api.interceptors.response.use((r) => r, (e) => {
  const d = e?.response?.data?.detail;
  if (e?.response?.status === 403 && typeof d === "string" && d.startsWith(REVOKED_PREFIX)) {
    sessionStorage.setItem(ACCESS_ERROR_KEY, d);
    localStorage.removeItem("pb_token");
    if (!window.location.pathname.startsWith("/login")) window.location.assign("/login");
  }
  return Promise.reject(e);
});

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Algo deu errado. Tente novamente.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  if (typeof d.msg === "string") return d.msg;
  return String(d);
}

export const brl = (v, d = 2) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(v) || 0);
export const brlUnit = (v) => (Math.abs(Number(v) || 0) < 0.1 && v !== 0 ? brl(v, 4) : brl(v));
export const pct = (v, d = 1) => (v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(d).replace(".", ",")}%`);
export const num = (v, d = 2) => new Intl.NumberFormat("pt-BR", { maximumFractionDigits: d }).format(Number(v) || 0);

export const UNITS = ["g", "kg", "ml", "l", "un", "m", "garrafa", "lata", "caixa"];
export const UNIT_LABELS = { g: "Grama (g)", kg: "Quilo (kg)", ml: "Mililitro (ml)", l: "Litro (l)", un: "Unidade (un)", m: "Metro (m)", garrafa: "Garrafa", lata: "Lata", caixa: "Caixa" };
export const EXPENSE_CATEGORIES = ["Ocupação", "Utilidades", "Serviços", "Marketing", "Manutenção", "Financeiro", "Outros"];

export const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${names[Number(mo) - 1]}/${y.slice(2)}`;
};
export const currentMonth = () => new Date().toISOString().slice(0, 7);

const UNIT_BASE = { kg: ["g", 1000], g: ["g", 1], l: ["ml", 1000], ml: ["ml", 1] };
export const unitPrice = (ing) => (ing ? (ing.package_price != null ? Number(ing.package_price) / (Number(ing.package_qty) || 1) : Number(ing.price_per_unit) || 0) : 0);
const BULK = { g: ["kg", 1000], ml: ["l", 1000] };
export const bulkPrice = (ing) => {
  const [u, f] = BULK[ing.unit] || [ing.unit, 1];
  const qty = Number(ing.package_qty) || 1;
  const fixQty = ["kg", "l"].includes(ing.unit) && qty >= 100 ? qty / 1000 : qty;
  const cost = ing.package_price != null ? (Number(ing.package_price) / fixQty) * f : unitPrice(ing) * f;
  return { cost, unit: u };
};
export function itemCost(ing, qty, unit) {
  if (!ing) return 0;
  const [bu, fi] = UNIT_BASE[unit] || [unit, 1];
  const [bi, fg] = UNIT_BASE[ing.unit] || [ing.unit, 1];
  const price = unitPrice(ing);
  if (bu !== bi) return qty * price;
  return (qty * fi) / fg * price;
}
export const channelPct = (c) => (c?.fees || []).reduce((a, f) => a + (Number(f.pct) || 0), 0);
export const channelCosts = (price, c) => (c?.fees || []).map((f) => ({ ...f, pct: Number(f.pct) || 0, amount: (price * (Number(f.pct) || 0)) / 100 }));
// Markup divisor: preço = custo ÷ (1 − (margem desejada + taxas do canal)). Ex.: 9,65 ÷ (1 − (0,60 + 0,17)) = 41,96
export const priceForMargin = (cost, c, target) => { if (!cost) return 0; const total = Math.min(95, (Number(target) || 0) + channelPct(c)); return cost / (1 - total / 100); };
// Preço no canal que mantém o mesmo retorno líquido do preço de balcão: balcão ÷ (1 − taxas). Ex.: 28,90 ÷ 0,83 = 34,82
export const pricePassingFees = (counterPrice, c) => { if (!counterPrice) return 0; return counterPrice / (1 - Math.min(95, channelPct(c)) / 100); };
export const marginImpossible = (c, target) => (Number(target) || 0) + channelPct(c) >= 95;
// Preço efetivo da ficha no canal: próprio (channel_prices) ou o preço padrão (sale_price)
export const effectivePrice = (r, c) => { const own = Number(r?.channel_prices?.[c?.id]); return own > 0 ? own : Number(r?.sale_price) || 0; };
export const hasOwnPrice = (r, c) => Number(r?.channel_prices?.[c?.id]) > 0;
export const marginStatus = (cmPct, target) => (cmPct == null ? null : cmPct >= (Number(target) || 0) ? "ok" : cmPct >= 0 ? "warn" : "bad");
export const STATUS_TEXT = { ok: "text-emerald-500", warn: "text-amber-500", bad: "text-rose-500" };
export const STATUS_BG = { ok: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30", warn: "bg-amber-500/15 text-amber-600 border-amber-500/30", bad: "bg-rose-500/15 text-rose-600 border-rose-500/30" };
export const contribution = (price, cost, c) => { if (!(price > 0)) return { cm: null, cmPct: null, varTotal: 0 }; const varTotal = (price * channelPct(c)) / 100; const cm = price - cost - varTotal; return { cm, cmPct: (cm / price) * 100, varTotal }; };
export function suggestedPrice(cost, s) {
  const p = Math.min(95, ["tax_pct", "card_fee_pct", "marketplace_pct", "other_pct", "target_margin_pct"].reduce((a, k) => a + (Number(s?.[k]) || 0), 0));
  return cost ? cost / (1 - p / 100) : 0;
}
export const feesPct = (s) => ["tax_pct", "card_fee_pct", "marketplace_pct", "other_pct"].reduce((a, k) => a + (Number(s?.[k]) || 0), 0);
